import { useEffect, useRef, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { ArrowLeft, ChartSpline, ExternalLink, FileSpreadsheet, MailX, RotateCcw, Truck } from 'lucide-react';
import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';
import {
    ActionsAndSave,
    PageActions,
    PageBreadcrumb,
    PageBreadcrumbSegment,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';

import {
    GetProductsToOrderOnceForSupplyOrderQuery,
    useUpdateManualSupplyOrderForecastsMutation,
    useUpdateManualSupplyOrderProductStockVariationsMutation,
    useUpdateSupplyOrderMutation,
    GetSupplyOrderSupplierProductsStockVariationDocument,
    GetSupplyOrderDetailsDocument,
    SupplyOrderState,
    TotadminPermission,
} from 'data/__generated__';

import { handleUpdateResults } from 'handlers';

import { AbortSupplyOrderPopup } from './Popups/AbortSupplyOrderPopup';
import { AdditionalDeliveryPopup } from './Popups/AdditionalDeliveryPopup';
import { ForecastsSection } from './sections/ForecastsSection';
import { IssuesSection } from './sections/IssuesSection';
import { OrderInfoSection } from './sections/OrderInfoSection';
import { OrderedProductsSection } from './sections/OrderedProductsSection';
import { ProductsToAddSection } from './sections/ProductsToAddSection';
import { ReceiptSection } from './sections/ReceiptSection';
import { StateHistorySection } from './sections/StateHistorySection';
import { SupplierSection } from './sections/SupplierSection';
import { SupplyOrderKeyFacts } from './sections/SupplyOrderKeyFacts';
import { SupplyOrderStateCard } from './sections/SupplyOrderStateCard';
import {
    getSupplyOrderFormValues,
    mapFormValuesToUpdateInput,
    SupplyOrderDetails,
    SupplyOrderFormValues,
} from './supplyOrderFormValues';
import { useSupplyOrderStateActions } from './useSupplyOrderStateActions';

export const SupplyOrderDetailsForm = ({
    supplyOrder,
    productsToOrderOnce,
}: {
    supplyOrder: SupplyOrderDetails;
    productsToOrderOnce: GetProductsToOrderOnceForSupplyOrderQuery['productsToOrderOnceForSupplyOrder'];
}) => {
    const [isAbortPopupOpen, setIsAbortPopupOpen] = useState(false);
    const [isAdditionalDeliveryPopupOpen, setIsAdditionalDeliveryPopupOpen] = useState(false);

    const [updateSupplyOrder, { loading: updateLoading }] = useUpdateSupplyOrderMutation();
    const [updateStockVariations, { loading: stockVariationsLoading }] =
        useUpdateManualSupplyOrderProductStockVariationsMutation({
            variables: { supplyOrderIds: [supplyOrder._id] },
            refetchQueries: [GetSupplyOrderSupplierProductsStockVariationDocument],
        });
    const [updateForecasts, { loading: forecastsLoading }] = useUpdateManualSupplyOrderForecastsMutation({
        variables: { supplyOrderIds: [supplyOrder._id] },
        refetchQueries: [GetSupplyOrderDetailsDocument],
    });

    const methods = useForm<SupplyOrderFormValues>({ defaultValues: getSupplyOrderFormValues(supplyOrder) });
    const {
        control,
        formState: { isDirty, isSubmitting, isSubmitted },
        handleSubmit,
        reset,
    } = methods;

    // UN SEUL `useFieldArray` sur `products` pour toute la page : react-hook-form ne synchronise pas
    // deux instances portant le même nom — un produit ajouté depuis « Produits à commander » n'arrivait
    // jamais dans le tableau des produits commandés.
    const { fields: productFields, append: appendProduct } = useFieldArray({ control, name: 'products' });

    // Réaligner le formulaire sur la base quand l'achat change sous nos pieds (changement de statut,
    // modification par quelqu'un d'autre). On compare `updatedAt` plutôt que l'identité de l'objet :
    // Apollo renvoie un nouvel objet à chaque écriture de cache, y compris quand rien n'a bougé —
    // l'ancienne fiche se réinitialisait alors en pleine saisie.
    const previousUpdatedAt = useRef(supplyOrder.updatedAt);
    useEffect(() => {
        if (isSubmitting) {
            return;
        }
        if (isSubmitted) {
            previousUpdatedAt.current = supplyOrder.updatedAt;
            reset(getSupplyOrderFormValues(supplyOrder));
            return;
        }
        if (supplyOrder.updatedAt !== previousUpdatedAt.current) {
            previousUpdatedAt.current = supplyOrder.updatedAt;
            reset(getSupplyOrderFormValues(supplyOrder));
        }
    }, [supplyOrder, reset, isSubmitting, isSubmitted]);

    const wasAlreadyOrdered = supplyOrder.stateHistory.some(({ state }) => state === SupplyOrderState.Ordered);
    const {
        actions,
        loading: stateActionLoading,
        markAsOrderedWithoutEmail,
        missingOdooSupplierPopup,
    } = useSupplyOrderStateActions({
        supplyOrderId: supplyOrder._id,
        supplier: supplyOrder.supplier,
        state: supplyOrder.state,
        wasAlreadyOrdered,
        onAbort: () => setIsAbortPopupOpen(true),
    });

    const onSave = handleSubmit(async (formValues) => {
        const { data } = await updateSupplyOrder({
            variables: { supplyOrderId: supplyOrder._id, fields: mapFormValuesToUpdateInput(formValues) },
        });

        if (!data) {
            throw new Error("Une erreur est survenue lors de la modification de l'achat");
        }

        const { success, formErrors, errors, warnings } = data.updateSupplyOrderMutation;

        formErrors?.forEach(({ sectionName, sectionErrors }) =>
            toast.error(
                <span>
                    Erreur dans la section « {sectionName} » :
                    {sectionErrors.map(({ fieldName, fieldError }, index) => (
                        <span key={index}>
                            <br />- {fieldName} : « {fieldError} »
                        </span>
                    ))}
                </span>,
                { autoClose: false },
            ),
        );
        errors.forEach((error) => toast.error(<span>Erreur : {error}</span>, { autoClose: false }));
        warnings.forEach((warning) => toast.warning(<span>Attention : {warning}</span>));

        if (success) {
            toast.success(
                `L'achat n°${supplyOrder.number} au fournisseur « ${supplyOrder.supplier.name} » a bien été modifié !`,
            );
            return;
        }

        if (!errors.length && !formErrors?.length) {
            throw new Error("Une erreur inconnue s'est produite");
        }
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Achat',
            items: [
                {
                    label: 'Réinitialiser les modifications',
                    icon: <RotateCcw />,
                    onClick: () => reset(),
                    disabled: !isDirty,
                },
                {
                    label: 'Déclarer une livraison additionnelle',
                    icon: <Truck />,
                    onClick: () => setIsAdditionalDeliveryPopupOpen(true),
                    disabled: isDirty,
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                },
                {
                    label: 'Passer en « Commandé » sans envoyer le mail',
                    icon: <MailX />,
                    onClick: () => void markAsOrderedWithoutEmail(),
                    disabled: isDirty || supplyOrder.state !== SupplyOrderState.Ready,
                    dataTest: 'mark-as-ordered-without-email',
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                },
            ],
        },
        {
            label: 'Prédictions',
            items: [
                {
                    label: 'Mettre à jour les variations de stock',
                    icon: <ChartSpline />,
                    disabled: stockVariationsLoading,
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    onClick: async () => {
                        const { data } = await updateStockVariations();
                        handleUpdateResults({
                            data: data?.updateManualSupplyOrderProductStockVariations,
                            successMessage: 'Les variations de stock ont été recalculées',
                        });
                    },
                },
                {
                    label: 'Mettre à jour les prédictions',
                    icon: <ChartSpline />,
                    disabled: forecastsLoading,
                    requiredPermissions: [TotadminPermission.SupplyOrdersManage],
                    onClick: async () => {
                        const { data } = await updateForecasts();
                        handleUpdateResults({
                            data: data?.updateManualSupplyOrderForecasts,
                            successMessage: 'Les prédictions ont été recalculées',
                        });
                    },
                },
            ],
        },
        {
            label: 'Liens',
            items: [
                ...(supplyOrder.spreadsheetId
                    ? [
                          {
                              label: 'Bon de commande (Google Sheet)',
                              icon: <FileSpreadsheet />,
                              onClick: () =>
                                  window.open(
                                      `https://docs.google.com/spreadsheets/d/${supplyOrder.spreadsheetId}`,
                                      '_blank',
                                      'noopener,noreferrer',
                                  ),
                          },
                      ]
                    : []),
                ...(supplyOrder.odooPurchaseOrderUrl
                    ? [
                          {
                              label: 'Bon de commande Odoo',
                              icon: <ExternalLink />,
                              onClick: () =>
                                  window.open(supplyOrder.odooPurchaseOrderUrl ?? '', '_blank', 'noopener,noreferrer'),
                          },
                      ]
                    : []),
                ...(supplyOrder.odooReceiptPickingUrl
                    ? [
                          {
                              label: 'Bon de réception Odoo',
                              icon: <ExternalLink />,
                              onClick: () =>
                                  window.open(supplyOrder.odooReceiptPickingUrl ?? '', '_blank', 'noopener,noreferrer'),
                          },
                      ]
                    : []),
                {
                    label: 'Fiche fournisseur',
                    icon: <ExternalLink />,
                    to: `/supplier/${supplyOrder.supplier._id}`,
                    requiredPermissions: [TotadminPermission.SuppliersRead],
                },
            ],
        },
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste des achats', icon: <ArrowLeft />, to: PAGES.supplyOrders.url }],
        },
    ];

    return (
        <Container data-test="supply-order-details">
            <Helmet title={`Achat n°${supplyOrder.number} · ${supplyOrder.supplier.name} · TOTADMIN`} />
            <FormProvider {...methods}>
                <Form>
                    <PageHeader>
                        <PageBreadcrumb>
                            <PageBreadcrumbSegment label="Achats" to={PAGES.supplyOrders.url} />
                            <PageBreadcrumbSegment
                                label={`Achat n°${supplyOrder.number}`}
                                kicker={supplyOrder.supplier.name}
                                isCurrent
                                dataTest="supply-order-breadcrumb"
                            />
                        </PageBreadcrumb>
                        <PageActions>
                            <ActionsAndSave
                                actionGroups={actionGroups}
                                onSave={onSave}
                                saveDisabled={!isDirty}
                                saveLoading={updateLoading}
                                saveTitle="Enregistrer l'achat"
                                saveDataTest="submit-button"
                                saveRequiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                            />
                        </PageActions>
                    </PageHeader>

                    <Content>
                        <ScrollableContent>
                            <SupplyOrderStateCard
                                state={supplyOrder.state}
                                actions={actions}
                                loading={stateActionLoading}
                                isDirty={isDirty}
                            />
                            <SupplyOrderKeyFacts supplyOrder={supplyOrder} />
                            <OrderedProductsSection
                                supplyOrder={supplyOrder}
                                productsToOrderOnce={productsToOrderOnce}
                                productFields={productFields}
                            />
                            <OrderInfoSection supplyOrder={supplyOrder} />
                            <ReceiptSection supplyOrder={supplyOrder} />
                            <IssuesSection supplyOrder={supplyOrder} />
                            <ProductsToAddSection
                                supplyOrder={supplyOrder}
                                productFields={productFields}
                                onAddProduct={appendProduct}
                            />
                            <ForecastsSection supplyOrder={supplyOrder} />
                            <SupplierSection supplyOrder={supplyOrder} />
                            <StateHistorySection supplyOrder={supplyOrder} />
                            <AuditLogsSection collectionDocumentIds={[supplyOrder._id]} />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>

            {missingOdooSupplierPopup}
            <AbortSupplyOrderPopup
                isOpen={isAbortPopupOpen}
                setIsOpen={setIsAbortPopupOpen}
                supplyOrderId={supplyOrder._id}
            />
            <AdditionalDeliveryPopup
                isOpen={isAdditionalDeliveryPopupOpen}
                setIsOpen={setIsAdditionalDeliveryPopupOpen}
                supplyOrderId={supplyOrder._id}
            />
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
